import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

const CaseTimelineStatistics = ({ title = 'Case Assignment Status Summary' }) => {
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const rolesRaw = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const roles = Array.isArray(rolesRaw) ? rolesRaw.map(r => String(r).toLowerCase()) : [];

                const params = {};
                const orgId = storedUser?.employee?.organization_id || localStorage.getItem('organization_id');
                if (orgId) params.organization_id = orgId;
                const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
                const needToPassUserId = roles.some(r => targetEmployee_id_Roles.includes(r));
                if (needToPassUserId) {
                    const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
                    if (employeeId) params.assigned_to_officer_id = employeeId;
                }

                const res = await DashboardServices.getDashboardCaseAssignmentStatusSummary(params);
                const payload = res?.data.view?.data ?? res;

                const keyLabelMap = {
                    ongoing_assignments:     { stage: 'IN_PROGRESS',  label: 'Ongoing Cases' },
                    resolved_assignments:    { stage: 'FINISHED',     label: 'Resolved Cases' },
                    rejected_assignments:    { stage: 'REJECTED',     label: 'Rejected Cases' },
                    pending_assignments:     { stage: 'SUBMITTED',    label: 'Pending Cases' },
                    accepted_assignments:    { stage: 'ASSIGNED',     label: 'Accepted Cases' },
                    completed_assignments:   { stage: 'CLOSED',       label: 'Completed Cases' },
                    transferred_assignments: { stage: 'UNDER_REVIEW', label: 'Transferred Cases' },
                    reassigned_assignments:  { stage: 'REASSIGNED',   label: 'Reassigned Cases' },
                };

                const formatted = Object.entries(keyLabelMap).map(([key, { stage, label }]) => ({
                    stage,
                    label,
                    stage_count: payload?.[key] ?? 0,
                }));

                if (mounted) {
                    setStatistics(formatted);
                }
            } catch (err) {
                console.error('Failed to load case timeline statistics', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    const getStageColor = (stage) => {
        const colors = {
            'SUBMITTED':    '#9c27b0',
            'ASSIGNED':     '#2196f3',
            'IN_PROGRESS':  '#ff9800',
            'FINISHED':     '#4caf50',
            'REJECTED':     '#f44336',
            'CLOSED':       '#607d8b',
            'UNDER_REVIEW': '#00bcd4',
            'REASSIGNED':   '#795548',
        };
        return colors[stage] || '#757575';
    };

    if (loading) {
        return (
            <WidgetCard sx={{ minHeight: 200 }}>
                <CenteredSpinner />
            </WidgetCard>
        );
    }

    if (statistics.length === 0) {
        return (
            <WidgetCard sx={{ minHeight: 200 }}>
                <MainCardTitle>{title}</MainCardTitle>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            </WidgetCard>
        );
    }

    return (
        <WidgetCard sx={{ minHeight: 200 }}>
            <MainCardTitle>{title}</MainCardTitle>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                {statistics.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={4} key={stat.stage || index}>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    mb: 1.5
                                }}
                            >
                                {stat.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 36,
                                    fontWeight: 700,
                                    color: getStageColor(stat.stage),
                                    lineHeight: 1
                                }}
                            >
                                {stat.stage_count}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </WidgetCard>
    );
};

export default CaseTimelineStatistics;